'use client';

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Tag,
  Typography,
  Row,
  Col,
  Space,
  Button,
  Input,
  Select,
  Card,
} from 'antd';
import {
  HistoryOutlined,
  ReloadOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  DatabaseOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useRouter } from 'next/navigation';
import { InventoryTransactionRecord } from '@/lib/mockGrStore';

const { Title, Text } = Typography;

export default function InventoryTransactionsPage() {
  const router = useRouter();

  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<InventoryTransactionRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/inventory/transactions');
      const json = await res.json();
      if (json.success) {
        setTransactions(json.data);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Overall Transaction Stats
  const totalTransactionsCount = transactions.length;
  const totalQuantityIn = transactions.reduce((sum, t) => sum + t.quantity, 0);
  const purchaseReceiptsCount = transactions.filter((t) => t.txType === 'PURCHASE_RECEIPT').length;
  const uniqueItemsCount = new Set(transactions.map((t) => t.itemId)).size;

  // Filter transactions
  const filteredTransactions = transactions.filter((t) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      t.id.toLowerCase().includes(q) ||
      t.itemCode.toLowerCase().includes(q) ||
      t.itemName.toLowerCase().includes(q) ||
      t.grNumber.toLowerCase().includes(q) ||
      t.poNumber.toLowerCase().includes(q);

    if (!matchesSearch) return false;

    if (typeFilter !== 'ALL' && t.txType !== typeFilter) {
      return false;
    }

    return true;
  });

  const columns = [
    {
      title: 'ID Transaksi',
      dataIndex: 'id',
      key: 'id',
      width: 170,
      render: (id: string) => (
        <Tag
          color="geekblue"
          style={{ fontWeight: 700, borderRadius: 6, fontSize: 12, padding: '2px 8px' }}
        >
          {id}
        </Tag>
      ),
    },
    {
      title: 'Waktu Transaksi',
      dataIndex: 'txDate',
      key: 'txDate',
      width: 180,
      render: (d: string) => (
        <span style={{ fontSize: 12, color: '#334155' }}>
          {new Date(d).toLocaleDateString('id-ID', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      ),
    },
    {
      title: 'Tipe Mutasi',
      dataIndex: 'txType',
      key: 'txType',
      width: 170,
      render: (t: string) => (
        <Tag
          color="green"
          style={{
            fontWeight: 600,
            borderRadius: 6,
            fontSize: 11,
            padding: '2px 8px',
          }}
        >
          {t}
        </Tag>
      ),
    },
    {
      title: 'Item Barang',
      key: 'item',
      render: (_: unknown, r: InventoryTransactionRecord) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0F172A', fontSize: 13 }}>{r.itemName}</div>
          <div style={{ color: '#6366F1', fontSize: 12, marginTop: 2, fontWeight: 600 }}>
            {r.itemCode}
          </div>
        </div>
      ),
    },
    {
      title: 'Kuantitas Masuk',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right' as const,
      width: 160,
      render: (q: number, r: InventoryTransactionRecord) => (
        <Tag
          color="cyan"
          style={{
            fontWeight: 800,
            fontSize: 13,
            padding: '3px 10px',
            borderRadius: 6,
          }}
        >
          +{q.toLocaleString('id-ID')} {r.uomCode}
        </Tag>
      ),
    },
    {
      title: 'Dokumen Asal (GR & PO)',
      key: 'docs',
      width: 220,
      render: (_: unknown, r: InventoryTransactionRecord) => (
        <div style={{ fontSize: 12 }}>
          <div>
            Bukti GR:{' '}
            <strong
              style={{ color: '#0284C7', cursor: 'pointer' }}
              onClick={() => router.push(`/goods-receipts`)}
            >
              {r.grNumber}
            </strong>
          </div>
          <div style={{ marginTop: 2 }}>
            Pesanan PO:{' '}
            <strong
              style={{ color: '#7C3AED', cursor: 'pointer' }}
              onClick={() => router.push(`/purchase-orders`)}
            >
              {r.poNumber}
            </strong>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      {/* Header Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Title level={3} style={{ margin: 0, color: '#0F172A', fontWeight: 800 }}>
              Buku Mutasi Transaksi Persediaan
            </Title>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#0369A1',
                background: '#E0F2FE',
                border: '1px solid #BAE6FD',
                padding: '3px 10px',
                borderRadius: 20,
              }}
            >
              Audit Log Stok
            </span>
          </div>
          <Text type="secondary" style={{ fontSize: 13, marginTop: 4, display: 'block' }}>
            Rekam jejak kronologis seluruh mutasi penambahan barang masuk dari Goods Receipt (GR) secara atomik.
          </Text>
        </div>

        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => router.push('/inventory')}
            style={{ borderRadius: 10 }}
          >
            Unit Stok Fisik
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchTransactions} style={{ borderRadius: 10 }}>
            Segarkan
          </Button>
        </Space>
      </div>

      {/* KPI Stat Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #EEF2FF 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #E0E7FF',
              boxShadow: '0 4px 16px -2px rgba(99, 102, 241, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase' }}>
              Total Transaksi Mutasi
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#1E1B4B', marginTop: 4 }}>
              {totalTransactionsCount}
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Semua mutasi tercatat</div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #BBF7D0',
              boxShadow: '0 4px 16px -2px rgba(16, 185, 129, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase' }}>
              Penerimaan GR (Purchase Receipt)
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#064E3B', marginTop: 4 }}>
              {purchaseReceiptsCount}{' '}
              <span style={{ fontSize: 13, fontWeight: 600, color: '#15803D' }}>Transaksi</span>
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Mutasi barang masuk dari vendor</div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #BAE6FD',
              boxShadow: '0 4px 16px -2px rgba(14, 165, 233, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#0369A1', textTransform: 'uppercase' }}>
              Total Kuantitas Masuk
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#0C4A6E', marginTop: 4 }}>
              +{totalQuantityIn.toLocaleString('id-ID')}
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Total unit fisik diterima</div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFFBEB 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #FDE68A',
              boxShadow: '0 4px 16px -2px rgba(245, 158, 11, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#B45309', textTransform: 'uppercase' }}>
              Item Terlibat
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#78350F', marginTop: 4 }}>
              {uniqueItemsCount}{' '}
              <span style={{ fontSize: 13, fontWeight: 600, color: '#B45309' }}>Varian Item</span>
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Tercatat dalam mutasi</div>
          </div>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card
        style={{
          borderRadius: 16,
          border: '1px solid #F1F5F9',
          boxShadow: '0 4px 20px -2px rgba(99, 102, 241, 0.04)',
        }}
        styles={{ body: { padding: '18px 20px' } }}
      >
        {/* Search & Filter Bar */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12}>
            <Input
              placeholder="Cari berdasarkan ID Transaksi, Kode, Nama Barang, GR #, atau PO #..."
              prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 10 }}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: '100%', borderRadius: 10 }}
              options={[
                { value: 'ALL', label: 'Semua Tipe Mutasi' },
                { value: 'PURCHASE_RECEIPT', label: 'Penerimaan GR (Purchase Receipt)' },
                { value: 'ADJUSTMENT', label: 'Penyesuaian (Adjustment)' },
              ]}
            />
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredTransactions}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          size="middle"
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
}
